import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useQueryClient } from "@tanstack/react-query";
import { FormLayout } from "antd/lib/form/Form";
import { useDeleteTimeslotMutation, useUpdateTimeslotMutation } from "api/mutations/timeslots";
import { useGetForumByIdQuery } from "api/queries/forums";
import KEYS from "api/queries/keys";
import { useGetTimeslotQuery } from "api/queries/timeslots";
import { Button, Divider, Form, Input, Modal, Select, Space, Spin, Typography } from "components/styleguide";
import TimeSlotTypes, { TimeslotType } from "enums/TimeSlotType";
import {
  ForumResponseModel,
  TimeSlotApiForumForumIdTimeslotTimeSlotIdDeleteRequest,
  TimeSlotApiForumForumIdTimeslotTimeSlotIdPutRequest,
  TimeSlotResponseModel,
  TimeSlotSessionResponseModel,
  TimeSlotUpdateRequestModel,
} from "generated/api";
import { toNumber } from "lodash";
import moment, { MomentInput } from "moment/moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { toWallClockMoment } from "utils/dateUtils";

import {
  applyFormValuesToSessions as applySessionFormValuesToSessions,
  applySequentialOrder,
  createDefaultSession,
  createSessionWithDefaultDuration,
  DuplicateTimePeriod,
  getDuplicateSessionTimePeriods,
  getDuplicateTimePeriods,
  getDuplicateTimeSlotSessionIds,
  getDuplicateTimeSlotValidationMessage,
  getExistingDuplicateTimeSlotSessionIds,
  getSessionKey,
  getSessionTimePeriods,
  handleTimeslotError,
  hasDuplicateSessionTimePeriodWithSavedSession,
  isDuplicateSessionError,
  isDuplicateTimeSlotError,
  normalizeSessionOrder,
  showDuplicateSessionTimeValidationError,
  showDuplicateTimeSlotValidationError,
  showTimeslotValidationError,
} from "../helpers";
import useUnsavedChangesPrompt from "../useUnsavedChangesPrompt";
import SessionDependencyDrawer from "./SessionDependencyDrawer";
import SessionTable from "./SessionsTable";

const EditTimeslot = () => {
  const { t } = useTranslation();
  const timeslotId = toNumber(useParams().timeslotId);
  const forumId = toNumber(useParams().id);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [timeslotForm] = Form.useForm();
  const queryClient = useQueryClient();

  const [formLayout] = useState<FormLayout>("inline");
  const [timeslot, setTimeslot] = useState<TimeSlotResponseModel>();
  const [sessions, setSessions] = useState<TimeSlotSessionResponseModel[]>([]);
  const [forum, setForum] = useState<ForumResponseModel>();
  const [disableOrdering, setDisableOrdering] = useState<boolean>(false);
  const [defaultSessionDuration, setDefaultSessionDuration] = useState<number>(0);
  const [duplicateSessionIds, setDuplicateSessionIds] = useState<number[]>([]);
  const [duplicateTimePeriods, setDuplicateTimePeriods] = useState<DuplicateTimePeriod[]>([]);
  const [duplicateMessage, setDuplicateMessage] = useState<string | undefined>();
  const [hasDuplicateTimeslotError, setHasDuplicateTimeslotError] = useState<boolean>(false);
  const [dependencySession, setDependencySession] = useState<TimeSlotSessionResponseModel | undefined>();

  const updateTimeslotMutation = useUpdateTimeslotMutation();
  const deleteTimeslotMutation = useDeleteTimeslotMutation();
  const [isTimeslotDirty, setIsTimeslotDirty] = useState<boolean>(false);
  const [hasSessionFormChanges, setHasSessionFormChanges] = useState<boolean>(false);
  const [isTimeslotDeleteMode, setIsTimeslotDeleteMode] = useState<boolean>(false);
  const [sessionChangeVersion, setSessionChangeVersion] = useState<number>(0);
  const [originalSessions, setOriginalSessions] = useState<TimeSlotSessionResponseModel[]>([]);

  const formatComparableDate = (date?: MomentInput) => {
    if (!date) {
      return null;
    }

    const value = moment(date);
    return value.isValid() ? value.format("YYYY-MM-DDTHH:mm:ss") : String(date);
  };

  const applyFormValuesToSessions = (sourceSessions: TimeSlotSessionResponseModel[]) => {
    return applySessionFormValuesToSessions(form, sourceSessions);
  };

  const syncSessionFormValues = (sourceSessions: TimeSlotSessionResponseModel[]) => {
    const sessionValues = sourceSessions.reduce<Record<string, { duration: [MomentInput | null, MomentInput | null] }>>(
      (values, session) => ({
        ...values,
        [getSessionKey(session)]: {
          duration: [
            session.startTime ? toWallClockMoment(session.startTime) : null,
            session.endTime ? toWallClockMoment(session.endTime) : null,
          ],
        },
      }),
      {},
    );

    form.resetFields(["sessions"]);
    form.setFieldsValue({ sessions: sessionValues });
  };

  const getComparableSessions = (sourceSessions: TimeSlotSessionResponseModel[]) =>
    normalizeSessionOrder(sourceSessions).map((session) => ({
      id: (session.id as number) > 0 ? session.id : null,
      order: session.order,
      startTime: formatComparableDate(session.startTime),
      endTime: formatComparableDate(session.endTime),
    }));

  const areSessionsEqual = (
    firstSessions: TimeSlotSessionResponseModel[],
    secondSessions: TimeSlotSessionResponseModel[],
  ) => JSON.stringify(getComparableSessions(firstSessions)) === JSON.stringify(getComparableSessions(secondSessions));

  const areTimeslotValuesEqual = (values: Partial<TimeSlotResponseModel>) => {
    if (!timeslot) {
      return false;
    }

    return (
      String(values.description ?? "") === String(timeslot.description ?? "") &&
      String(values.timeSlotType ?? "") === String(timeslot.timeSlotType ?? "")
    );
  };

  const isSessionLocked = (session?: TimeSlotSessionResponseModel) => {
    const dependencies = session?.dependencies;
    const itineraryItemsCount = dependencies?.itineraryItemsCount;
    const seminarSessionsCount = dependencies?.seminarSessionsCount;

    return !!(
      dependencies?.hasDependencies ||
      (typeof itineraryItemsCount === "number" && itineraryItemsCount > 0) ||
      (typeof seminarSessionsCount === "number" && seminarSessionsCount > 0)
    );
  };

  const markSessionChanged = () => {
    setDuplicateSessionIds([]);
    setDuplicateTimePeriods([]);
    setDuplicateMessage(undefined);
    setHasSessionFormChanges(true);
    setSessionChangeVersion((version) => version + 1);
  };

  const markTimeslotChanged = (_: Partial<TimeSlotResponseModel>, values: Partial<TimeSlotResponseModel>) => {
    setDuplicateSessionIds([]);
    setDuplicateTimePeriods([]);
    setDuplicateMessage(undefined);
    setHasDuplicateTimeslotError(false);
    setIsTimeslotDirty(!areTimeslotValuesEqual(values));
  };

  const handleError = (response?: unknown) => handleTimeslotError(response, t);

  useEffect(() => {
    if (!sessionChangeVersion) {
      return;
    }

    const duplicatePeriods = getDuplicateSessionTimePeriods(applyFormValuesToSessions(sessions));

    if (!duplicatePeriods.length) {
      return;
    }

    setDuplicateSessionIds([]);
    setDuplicateTimePeriods(duplicatePeriods);
    setDuplicateMessage(String(t("Duplicate session time")));
  }, [sessionChangeVersion]);

  const { isLoading: isTimeslotLoading } = useGetTimeslotQuery(
    { forumId, timeSlotId: timeslotId },
    {
      onSuccess: ({ data }) => {
        const normalizedSessions = normalizeSessionOrder(data.sessions ?? []);
        const hasPersistedSessions = normalizedSessions.length > 0;
        let sessionsToDisplay: TimeSlotSessionResponseModel[] = normalizedSessions;

        if (!hasPersistedSessions && forum) {
          sessionsToDisplay = [createDefaultSession(forum)];
        }

        const duplicatePeriods = getDuplicateSessionTimePeriods(normalizedSessions);

        setTimeslot(data);
        setSessions((currentSessions) => {
          const nextSessions =
            !hasPersistedSessions && !sessionsToDisplay.length && currentSessions.length
              ? currentSessions
              : sessionsToDisplay;

          setOriginalSessions(nextSessions);
          return nextSessions;
        });
        setDuplicateSessionIds([]);
        setDuplicateTimePeriods(duplicatePeriods);
        setDuplicateMessage(duplicatePeriods.length ? String(t("Session time already exists")) : undefined);
        setIsTimeslotDeleteMode(!hasPersistedSessions);
      },
      onError: (error: unknown) => handleError((error as { response?: unknown }).response),
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  const { isLoading: isForumLoading } = useGetForumByIdQuery(
    { forumId },
    {
      onSuccess: ({ data }) => {
        setSessions((currentSessions) => {
          if (currentSessions.length > 0) {
            return currentSessions;
          }

          const defaultSession = createDefaultSession(data);
          setOriginalSessions([defaultSession]);
          setIsTimeslotDeleteMode((deleteMode) => deleteMode || (!!timeslot && !(timeslot.sessions ?? []).length));
          return [defaultSession];
        });

        setForum(data);
      },
      onError: (error: unknown) => handleError((error as { response?: unknown }).response),
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const exitTimeslotDeleteMode = () => {
    setIsTimeslotDeleteMode(false);
  };

  const getSessionsFromForm = async () => {
    await form.validateFields();
    return applyFormValuesToSessions(sessions);
  };

  const showValidationError = () => showTimeslotValidationError(t);

  const showDuplicateTimeslotFieldsError = () => {
    setHasDuplicateTimeslotError(true);
    showDuplicateTimeSlotValidationError(t);
  };

  const isSaveLoading = updateTimeslotMutation.isLoading;
  const isDeleteLoading = deleteTimeslotMutation.isLoading;

  const mapSessionsToSaveRequest = (sourceSessions: TimeSlotSessionResponseModel[]) =>
    sourceSessions.map((session) => ({
      id: (session.id as number) > 0 ? session.id : null,
      startTime: session.startTime,
      endTime: session.endTime,
      order: session.order,
    }));

  const handleSubmit = async () => {
    let sessionsToSave: TimeSlotSessionResponseModel[] = [];

    try {
      sessionsToSave = await getSessionsFromForm();
      const duplicatePeriodsInForm = getDuplicateSessionTimePeriods(sessionsToSave);

      if (duplicatePeriodsInForm.length) {
        const duplicatesSavedSession = hasDuplicateSessionTimePeriodWithSavedSession(sessionsToSave);
        const duplicateMessageText = duplicatesSavedSession
          ? String(t("Session time already exists"))
          : String(t("Duplicate session time"));

        setDuplicateSessionIds([]);
        setDuplicateTimePeriods(duplicatePeriodsInForm);
        setDuplicateMessage(duplicateMessageText);
        if (duplicatesSavedSession) {
          toast.error(String(t("Session time already exists. Please update the highlighted session rows.")), {
            autoClose: 7500,
          });
        } else {
          showDuplicateSessionTimeValidationError(t);
        }
        return;
      }

      const timeSlotUpdateRequestModel: TimeSlotUpdateRequestModel = {
        description: timeslotForm.getFieldValue("description"),
        timeSlotType: timeslotForm.getFieldValue("timeSlotType"),
        disableOrdering,
        sessions: mapSessionsToSaveRequest(sessionsToSave),
      };

      const updateRequest: TimeSlotApiForumForumIdTimeslotTimeSlotIdPutRequest = {
        forumId,
        timeSlotId: timeslotId,
        timeSlotUpdateRequestModel,
      };
      const saveResponse = await updateTimeslotMutation.mutateAsync(updateRequest);
      const savedTimeslotData = saveResponse.data;
      const savedSessions = normalizeSessionOrder(savedTimeslotData.sessions ?? []);

      toast.success(t("Timeslot and sessions updated successfully"));
      form.resetFields(["sessions"]);
      setTimeslot(savedTimeslotData);
      setSessions(savedSessions);
      setOriginalSessions(savedSessions);
      setDuplicateSessionIds([]);
      setDuplicateTimePeriods([]);
      setDuplicateMessage(undefined);
      setHasDuplicateTimeslotError(false);
      setIsTimeslotDirty(false);
      setHasSessionFormChanges(false);
      setIsTimeslotDeleteMode(false);
      queryClient.invalidateQueries([KEYS.GET_TIMESLOTS]);
    } catch (e) {
      if ((e as { errorFields?: unknown[] })?.errorFields) {
        showValidationError();
        return;
      }

      const response = (e as { response?: unknown }).response;

      if (isDuplicateTimeSlotError(response)) {
        showDuplicateTimeslotFieldsError();
        return;
      }

      if (isDuplicateSessionError(response)) {
        const sessionIds = getDuplicateTimeSlotSessionIds(response);
        const existingSessionIds = getExistingDuplicateTimeSlotSessionIds(response);
        const timePeriods = getDuplicateTimePeriods(response);
        const duplicatePeriodsInForm = getDuplicateSessionTimePeriods(sessionsToSave);
        const duplicatePeriodsToHighlight = [...timePeriods, ...duplicatePeriodsInForm];

        setDuplicateSessionIds(sessionIds);
        setDuplicateMessage(
          existingSessionIds.length ? String(t("Session time already exists")) : String(t("Duplicate session time")),
        );
        setDuplicateTimePeriods(
          duplicatePeriodsToHighlight.length ? duplicatePeriodsToHighlight : getSessionTimePeriods(sessionsToSave),
        );
      }

      handleError(response);
    }
  };

  const handleSessionAdd = () => {
    const currentSessions = applyFormValuesToSessions(sessions);
    const newSession = createSessionWithDefaultDuration({
      forum,
      order: currentSessions.length + 1,
      defaultSessionDuration,
    });

    setSessions(applySequentialOrder([...currentSessions, newSession]));
    exitTimeslotDeleteMode();
    markSessionChanged();
  };

  const handleSessionDelete = (session: TimeSlotSessionResponseModel) => {
    setDuplicateSessionIds([]);
    setDuplicateTimePeriods([]);
    setDuplicateMessage(undefined);
    const currentSessions = applyFormValuesToSessions(sessions);

    if (!session.id) {
      const newData = currentSessions.filter((item) => item.order !== session.order);
      const hasPersistedSessions = newData.some((item) => Number(item.id) > 0);
      const hasOnlyPlaceholderSession = newData.length === 1 && !Number(newData[0].id) && !newData[0].endTime;

      if (newData.length === 0 && forum) {
        const defaultSession = createDefaultSession(forum);
        const nextSessions = [defaultSession];

        syncSessionFormValues(nextSessions);
        setSessions(nextSessions);
        setIsTimeslotDeleteMode(true);
        markSessionChanged();
        return;
      }

      const nextSessions = applySequentialOrder(newData);

      syncSessionFormValues(nextSessions);
      setSessions(nextSessions);
      if (!hasPersistedSessions && hasOnlyPlaceholderSession) {
        setIsTimeslotDeleteMode(true);
      } else {
        exitTimeslotDeleteMode();
      }
      markSessionChanged();
      return;
    }

    const newData = currentSessions.filter((item) => item.id !== session.id);

    if (newData.length === 0 && forum) {
      const defaultSession = createDefaultSession(forum);
      const nextSessions = [defaultSession];

      syncSessionFormValues(nextSessions);
      setSessions(nextSessions);
      setIsTimeslotDeleteMode(true);
    } else {
      const nextSessions = applySequentialOrder(newData);

      syncSessionFormValues(nextSessions);
      setSessions(nextSessions);
      exitTimeslotDeleteMode();
    }

    markSessionChanged();
  };

  const handleTimeslotDelete = () => {
    Modal.confirm({
      title: t("confirmation"),
      content: t("timeslotDeleteConfirm"),
      okText: t("yes"),
      okType: "danger",
      cancelText: t("no"),
      onOk: async () => {
        const deleteRequest: TimeSlotApiForumForumIdTimeslotTimeSlotIdDeleteRequest = {
          forumId,
          timeSlotId: timeslotId,
        };

        await deleteTimeslotMutation.mutateAsync(deleteRequest, {
          onSuccess: () => {
            toast.success(t("timeslotDeleteSuccess"));
            queryClient.invalidateQueries([KEYS.GET_TIMESLOTS]);
            queryClient.invalidateQueries([KEYS.GET_TIMESLOT]);
            navigate("..");
          },
          onError: (error: unknown) => handleError((error as { response?: unknown }).response),
        });
      },
    });
  };

  const timeTypesOptions = Object.keys(TimeSlotTypes).map((k) => ({
    value: k,
    label: t(`timeTypes.${TimeSlotTypes[k as keyof TimeslotType]}`),
  }));

  const sessionsWithFormValues = applyFormValuesToSessions(sessions);
  const hasSessionChanges = hasSessionFormChanges && !areSessionsEqual(sessionsWithFormValues, originalSessions);
  const hasUnsavedChanges = isTimeslotDirty || hasSessionChanges;
  const hasLockedSessions = sessions.some(isSessionLocked);
  const hasOnlyPlaceholderSession =
    sessionsWithFormValues.length === 1 && !Number(sessionsWithFormValues[0].id) && !sessionsWithFormValues[0].endTime;
  const canDeleteTimeslot = isTimeslotDeleteMode && hasOnlyPlaceholderSession;
  const isSaveDisabled = isSaveLoading || isDeleteLoading || !hasUnsavedChanges || canDeleteTimeslot;

  useUnsavedChangesPrompt(hasUnsavedChanges, isSaveLoading || isDeleteLoading, t("unsavedChangesPrompts.timeSlotEdit"));

  if (isForumLoading || !timeslot || isTimeslotLoading) {
    return (
      <div className="loader">
        <Spin />
      </div>
    );
  }

  const handleSessionReorder = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) {
      return;
    }

    const currentSessions = applyFormValuesToSessions(sessions);
    const orderedSessions = normalizeSessionOrder(currentSessions);
    const activeSession = orderedSessions.find((item) => String(item.order) === String(event.active.id));
    const overSession = orderedSessions.find((item) => String(item.order) === String(event.over?.id));

    if (!activeSession || !overSession || isSessionLocked(activeSession) || isSessionLocked(overSession)) {
      return;
    }

    const unlockedSessions = orderedSessions.filter((item) => !isSessionLocked(item));
    const oldIndex = unlockedSessions.findIndex((item) => String(item.order) === String(event.active.id));
    const newIndex = unlockedSessions.findIndex((item) => String(item.order) === String(event.over?.id));

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const reorderedUnlockedSessions = arrayMove(unlockedSessions, oldIndex, newIndex);
    let unlockedIndex = 0;
    const reorderedSessions = orderedSessions.map((session) => {
      if (isSessionLocked(session)) {
        return session;
      }

      const reorderedSession = reorderedUnlockedSessions[unlockedIndex];
      unlockedIndex += 1;
      return reorderedSession;
    });

    setSessions(applySequentialOrder(reorderedSessions));
    markSessionChanged();
  };

  return (
    <div id="edit-timeslot-page">
      <div className="section">
        <Form
          onFinish={handleSubmit}
          onFinishFailed={showValidationError}
          onValuesChange={markTimeslotChanged}
          id="edit-timeslot-form"
          layout={formLayout}
          form={timeslotForm}
          initialValues={{
            description: timeslot.description,
            timeSlotType: timeslot.timeSlotType,
          }}
          style={{ width: "100%" }}
        >
          <Form.Item
            label={t("description")}
            name="description"
            validateStatus={hasDuplicateTimeslotError ? "error" : undefined}
            rules={[
              {
                required: true,
                message: t("errors.required", {
                  prop: "$t(timeslotDescription)",
                }),
              },
            ]}
          >
            <Input style={{ width: 200 }} />
          </Form.Item>
          <Form.Item
            label={t("timeType")}
            name="timeSlotType"
            validateStatus={hasDuplicateTimeslotError ? "error" : undefined}
            rules={[
              {
                required: true,
                message: t("errors.required", { prop: "$t(timeType)" }),
              },
            ]}
          >
            <Select
              className="time-type-select"
              disabled={hasLockedSessions}
              style={{ width: 200 }}
              options={timeTypesOptions}
            />
          </Form.Item>
          <Space style={{ marginLeft: "auto" }}>
            <Form.Item>
              <Button
                danger
                loading={isDeleteLoading}
                disabled={isDeleteLoading || !canDeleteTimeslot}
                onClick={handleTimeslotDelete}
              >
                {t("Delete Timeslot")}
              </Button>
            </Form.Item>
            <Form.Item>
              <Button htmlType="submit" type="primary" loading={isSaveLoading} disabled={isSaveDisabled}>
                {t("save")}
              </Button>
            </Form.Item>
          </Space>
          {hasDuplicateTimeslotError && (
            <div style={{ width: "100%", marginTop: 4 }}>
              <Typography.Text type="danger" role="alert">
                {getDuplicateTimeSlotValidationMessage(t)}
              </Typography.Text>
            </div>
          )}
        </Form>
      </div>
      <Divider />
      <div className="section">
        <SessionTable
          form={form}
          data={sessions}
          onAdd={handleSessionAdd}
          onDelete={handleSessionDelete}
          onInfo={setDependencySession}
          onDragEnd={handleSessionReorder}
          disableOrdering={disableOrdering}
          setDisableOrdering={setDisableOrdering}
          rowKey="order"
          isSaveLoading={isSaveLoading}
          loading={isTimeslotLoading || isForumLoading}
          title={t("Sessions")}
          description={t("Add at least 1 session to your Timeslot")}
          defaultSessionDuration={defaultSessionDuration}
          onDefaultSessionDurationChange={(value) => setDefaultSessionDuration(value || 0)}
          onSessionValuesChange={markSessionChanged}
          duplicateSessionIds={duplicateSessionIds as unknown as never[]}
          duplicateTimePeriods={duplicateTimePeriods as unknown as never[]}
          duplicateMessage={duplicateMessage}
        />
      </div>
      <SessionDependencyDrawer
        open={!!dependencySession}
        timeslotId={timeslotId}
        timeSlotType={timeslot.timeSlotType}
        session={dependencySession}
        onClose={() => setDependencySession(undefined)}
      />
    </div>
  );
};

export default EditTimeslot;
